import { Texture } from "pixi.js";
import { EMBLEM, type Emblem, EmblemOpType, emblemKey } from "@common/utils/emblem";

const hex = (c: number): string => `#${c.toString(16).padStart(6, "0")}`;

/**
 * Draws an emblem onto a 2D context. `size` is the canvas size in pixels (the emblem's own
 * coordinate space is 0..255, so everything is scaled by size / 256). Drawing is clipped to the
 * body circle so nothing sticks out of the player.
 */
export function drawEmblem(ctx: CanvasRenderingContext2D, emblem: Emblem | undefined, size: number, clip = true): void {
    const k = size / EMBLEM.size;
    ctx.save();
    if (clip) {
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size * 0.47, 0, Math.PI * 2);
        ctx.clip();
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const op of emblem ?? []) {
        if (op.t === EmblemOpType.Rect) {
            ctx.globalCompositeOperation = "source-over";
            if (op.f) {
                ctx.fillStyle = hex(op.c);
                ctx.fillRect(op.x * k, op.y * k, op.sx * k, op.sy * k);
            } else {
                ctx.strokeStyle = hex(op.c);
                ctx.lineWidth = op.w * k;
                ctx.lineJoin = "miter";
                ctx.strokeRect(op.x * k, op.y * k, op.sx * k, op.sy * k);
            }
        } else if (op.t === EmblemOpType.Circle) {
            ctx.globalCompositeOperation = "source-over";
            ctx.beginPath();
            ctx.arc(op.x * k, op.y * k, op.r * k, 0, Math.PI * 2);
            if (op.f) {
                ctx.fillStyle = hex(op.c);
                ctx.fill();
            } else {
                ctx.strokeStyle = hex(op.c);
                ctx.lineWidth = op.w * k;
                ctx.stroke();
            }
        } else {
            ctx.globalCompositeOperation = op.t === EmblemOpType.Eraser ? "destination-out" : "source-over";
            ctx.strokeStyle = hex(op.c);
            ctx.lineWidth = op.w * k;
            const p = op.p;
            ctx.beginPath();
            ctx.moveTo(p[0] * k, p[1] * k);
            if (p.length === 2) {
                ctx.lineTo(p[0] * k + 0.01, p[1] * k);
            } else {
                // smooth: curve through midpoints
                for (let i = 2; i + 1 < p.length; i += 2) {
                    const mx = (p[i - 2] + p[i]) / 2 * k;
                    const my = (p[i - 1] + p[i + 1]) / 2 * k;
                    ctx.quadraticCurveTo(p[i - 2] * k, p[i - 1] * k, mx, my);
                }
                ctx.lineTo(p[p.length - 2] * k, p[p.length - 1] * k);
            }
            ctx.stroke();
        }
    }
    ctx.restore();
}

const textureCache = new Map<string, Texture>();
/** distinct emblems kept drawn (a match has at most 30 players, a Token round's): past it, the one seen longest ago is let go */
export const EMBLEM_TEXTURE_CACHE_LIMIT = 200;

/**
 * A texture of the emblem alone (transparent background), cached per distinct emblem.
 *
 * Each one used to stay for as long as the page was open: Pixi's own cache kept every emblem picture it was given
 * (and the graphics card its copy), and emptying this cache at 200 only made the next ones be drawn again, so a long
 * session piled up every emblem seen. Now Pixi doesn't keep them, the least recently seen leaves this cache first,
 * and the graphics card lets go of an emblem nobody has shown for a minute (it is sent again if it's needed again).
 */
export function emblemTexture(emblem: Emblem): Texture {
    const key = emblemKey(emblem);
    let tex = textureCache.get(key);
    if (tex) {
        // seen again: the most recently seen go last
        textureCache.delete(key);
        textureCache.set(key, tex);
        return tex;
    }
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = EMBLEM.size;
    const ctx = canvas.getContext("2d");
    if (ctx) drawEmblem(ctx, emblem, EMBLEM.size);
    tex = Texture.from(canvas, true);
    tex.source.autoGarbageCollect = true;
    if (textureCache.size >= EMBLEM_TEXTURE_CACHE_LIMIT) {
        const oldest = textureCache.keys().next().value;
        if (oldest !== undefined) textureCache.delete(oldest);
    }
    textureCache.set(key, tex);
    return tex;
}

/** how many emblems are kept drawn (tests) */
export function emblemTextureCount(): number {
    return textureCache.size;
}
